/**
 * Единственная точка интерпретации styleConfig слоя: новая форма
 * (`visualizations[]`) и legacy (`type` + paint/layout + пер-виз ключи корня)
 * приводятся к массиву виз-блоков. Все читатели стиля (рендер, панель, легенда,
 * креатор) идут через этот резолвер. См. docs/layers/plan.md.
 */
import type {
  LayerStyleConfig,
  LayerVisualization,
  VisualizationKind,
} from '../types/layer.js';
import { getDefaultPaintForType } from './detectGeometryType.js';

export type GeometryCategory = 'point' | 'line' | 'polygon';

/** Геометрия, которую рисует kind. */
export function visualizationKindGeometry(kind: VisualizationKind): GeometryCategory {
  switch (kind) {
    case 'line':
      return 'line';
    case 'fill':
    case 'fill-extrusion':
    case 'outline':
      return 'polygon';
    default:
      return 'point';
  }
}

/**
 * Канонический z-порядок блоков (первый — низ): теплокарта под всем,
 * полигоны под линиями, линии под точками (паритет с generic-рендером и Felt).
 */
const KIND_RENDER_ORDER: readonly VisualizationKind[] = [
  'heatmap',
  'fill',
  'fill-extrusion',
  'outline',
  'line',
  'circle',
  'symbol',
  'preview-point',
];

/** Компаратор блоков по каноническому z-порядку (`KIND_RENDER_ORDER`). */
function byRenderOrder(a: LayerVisualization, b: LayerVisualization): number {
  return KIND_RENDER_ORDER.indexOf(a.kind) - KIND_RENDER_ORDER.indexOf(b.kind);
}

/** Блоки в каноническом z-порядке (первый — низ). Исходный массив не мутируется. */
export function orderVisualizationsForRender(
  visualizations: readonly LayerVisualization[],
): LayerVisualization[] {
  return [...visualizations].sort(byRenderOrder);
}

/** Generic-типы: сегодняшний рендер рисует при них ВСЕ геометрии (4 саб-слоя). */
const GENERIC_LEGACY_KINDS: ReadonlySet<VisualizationKind> = new Set([
  'circle',
  'line',
  'fill',
]);

/** Дефолтный блок геометрии (дефолт-paint как у generic-рендера). */
function defaultBlockForGeometry(geometry: GeometryCategory): LayerVisualization {
  const kind: VisualizationKind =
    geometry === 'point' ? 'circle' : geometry === 'line' ? 'line' : 'fill';
  return {
    id: legacyVizId(kind),
    kind,
    enabled: true,
    paint: getDefaultPaintForType(kind as 'circle' | 'line' | 'fill'),
    layout: {},
  };
}

export interface ResolveVisualizationsOptions {
  /**
   * Фактические геометрии слоя (из данных). Для legacy generic-типов резолвер
   * добавляет дефолт-блоки на геометрии без блока — паритет с сегодняшним
   * рендером, где circle/line/fill рисуют все 4 саб-слоя.
   */
  geometries?: ReadonlySet<GeometryCategory>;
}

/** Пер-виз ключи legacy-корня styleConfig — переезжают в блок активного типа. */
const LEGACY_PER_VIZ_KEYS = [
  'colorByProperty',
  'colorPalette',
  'colorMapping',
  'colorFallback',
  'colorByNumericRanges',
  'iconByProperty',
  'iconMapping',
  'previewParamName',
  'previewShape',
  'previewSize',
  'previewBorderWidth',
  'previewFreeBackground',
  'showFieldOfView',
  'fieldOfViewAngleProperty',
  'showSymbolRotation',
  'symbolRotationAngleProperty',
  'heightByProperty',
  'heightMultiplier',
  'heightFallback',
  'iconSizePx',
  'previewAnchor',
  'symbolOnPolygons',
] as const;

const VECTOR_KINDS: ReadonlySet<VisualizationKind> = new Set([
  'circle',
  'symbol',
  'preview-point',
  'heatmap',
  'line',
  'fill',
  'fill-extrusion',
  'outline',
]);

/** Детерминированный id legacy-блока: стабилен между вызовами (react keys, кэши рендера). */
function legacyVizId(kind: VisualizationKind): string {
  return `legacy-${kind}`;
}

/**
 * Один виз-блок из legacy-styleConfig: paint/layout + пер-виз ключи корня
 * (colorByProperty, previewShape, …) переезжают в блок заданного kind.
 */
function buildLegacyBlock(
  kind: VisualizationKind,
  styleConfig: LayerStyleConfig,
): LayerVisualization {
  const viz: LayerVisualization = {
    id: legacyVizId(kind),
    kind,
    enabled: true,
    paint: styleConfig.paint ?? {},
    layout: styleConfig.layout ?? {},
  };
  const source = styleConfig as unknown as Record<string, unknown>;
  const target = viz as unknown as Record<string, unknown>;
  for (const key of LEGACY_PER_VIZ_KEYS) {
    if (source[key] !== undefined) {
      target[key] = source[key];
    }
  }
  return viz;
}

/**
 * Kind'ы «точечного представления» слоя. Heatmap рисует точечную геометрию,
 * но точечным ПРЕДСТАВЛЕНИЕМ для классификации (креатор и т.п.) не считается.
 */
export const POINT_PRESENTATION_KINDS: ReadonlySet<VisualizationKind> = new Set([
  'circle',
  'symbol',
  'preview-point',
]);

/**
 * Главный point-блок слоя: первый enabled point-блок (circle | symbol |
 * preview-point) в порядке массива; heatmap не считается. Legacy-конфиг
 * предварительно конвертируется резолвером (блок = `type`); передайте
 * `options.geometries` (фактические геометрии данных), чтобы legacy-слой с
 * не-точечным `type`, но нарисованными точками, тоже дал точечный блок —
 * иначе смешанные легаси-слои (точки+линия без явных viz-блоков) молча
 * теряют point-представление в вызывающем коде.
 * null — точечного представления у слоя нет.
 */
export function getPrimaryPointVisualization(
  styleConfig: LayerStyleConfig | null | undefined,
  options?: ResolveVisualizationsOptions,
): LayerVisualization | null {
  return (
    resolveVisualizations(styleConfig, options).find(
      (viz) => viz.enabled && POINT_PRESENTATION_KINDS.has(viz.kind),
    ) ?? null
  );
}

/**
 * Есть ли у слоя enabled виз-блок данного kind. Для legacy-конфига —
 * эквивалент сравнения `styleConfig.type === kind` (с дефолтом circle).
 */
export function hasVisualizationKind(
  styleConfig: LayerStyleConfig | null | undefined,
  kind: VisualizationKind,
): boolean {
  return resolveVisualizations(styleConfig).some((viz) => viz.enabled && viz.kind === kind);
}

/**
 * Виз-блоки слоя: новая форма (`visualizations[]`) как есть, иначе — конверсия
 * legacy `type`+paint/layout в один блок. Для generic legacy-типов (circle/line/
 * fill) при переданных `options.geometries` добавляет дефолт-блоки на остальные
 * фактические геометрии (паритет с 4-саб-слойным рендером). Единственная точка
 * интерпретации styleConfig — см. docs/layers/plan.md.
 */
export function resolveVisualizations(
  styleConfig: LayerStyleConfig | null | undefined,
  options?: ResolveVisualizationsOptions,
): LayerVisualization[] {
  if (Array.isArray(styleConfig?.visualizations)) {
    return styleConfig.visualizations;
  }
  const legacyKind = (styleConfig?.type || 'circle') as VisualizationKind;
  if (!VECTOR_KINDS.has(legacyKind)) {
    return [];
  }
  const blocks = [buildLegacyBlock(legacyKind, styleConfig ?? ({} as LayerStyleConfig))];

  // Специализированные типы (heatmap/symbol/preview-point/fill-extrusion) сегодня
  // прячут остальные геометрии — конверсия сохраняет это поведение.
  if (GENERIC_LEGACY_KINDS.has(legacyKind) && options?.geometries) {
    const covered = visualizationKindGeometry(legacyKind);
    for (const geometry of options.geometries) {
      if (geometry !== covered) {
        blocks.push(defaultBlockForGeometry(geometry));
      }
    }
  }

  return blocks.sort(byRenderOrder);
}
